import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { floatToPercentageString } from '../../utils/DataFormatterUtils';
import { getUserTypeFromURL } from '../../utils/HistoryUtils';
import {
	getWalkthroughScoreObject,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import OWInsightPopover from '../ow_insight_popover/OWInsightPopover';
import { UNFINISHED_WORK_ORDER_DISPLAY_STATUS_DISPLAY_NAMES } from '../../constants/WorkOrderDisplayStatuses';
import WalkthroughStatusDisplay from '../walkthrough_status_display/WalkthroughStatusDisplay';
import DateTimeHover from '../date_time_component/DateTime';
import WalkthroughScoreDisplay from '../walkthrough_score_display/WalkthroughScoreDisplay';
import HyperLink from '../common/HyperLink';
import { isWalthroughTaskAnswered } from '../../utils/SiteSurveyUtils';
import WalkthroughTaskListScoreDisplay, {
	WalkthroughTaskListScorePill,
} from '../walkthrough_score_display/WalkthroughTaskListScoreDisplay';
import WalkthroughRowNotePreview from './walkthrough_row_note_preview/WalkthroughRowNotePreview';
import GridColumnSeparator from '../row_note_preview/GridColumnSeparator';
import renderWithHighlight from '../RenderHighlight';
import { collapseHighlightAnalyzerVariants } from '../../utils/SearchHighlightUtils';

require('./WalkthroughRowDisplay.less');

const displayedFields = {
	title: 'Title',
	locationName: 'Location Name',
	id: 'Walkthrough ID',
	createdAt: 'Created At',
	completedAt: 'Completed At',
};

// Labels for every highlightable field the backend can return (see WalkthroughOpenSearchHelper
// getAllHighlightingFields + the taskAnswers inner_hits) that isn't already shown inline
// (title/locationName) or in its own section (notes). Root-field highlights arrive in the highlight
// bag; the nested taskAnswers matches (answer/taskListAnswers/checklistAnswers/comments) are merged by
// the backend and folded in under `taskAnswers` by walkthroughSearchAndAPIAdaptor.
const highlightLabels = {
	description: 'Description',
	// siteSurveyTitle is intentionally omitted: the walkthrough title shown in the first row already
	// surfaces the same highlighted text, so a separate "Site Survey" pill would be redundant.
	supplierFacilityDisplayName: 'Supplier',
	scheduledBy: 'Scheduled By',
	completedBy: 'Completed By',
	createdBy: 'Created By',
	// taskAnswers is rendered separately (below) as clickable pills that deep-link to the matched task
	// on the detail page, so it is intentionally not part of the generic (plain-text) highlight labels.
};

// taskAnswers is `nested` with several searchable sub-fields; the backend tags each highlight entry
// with the sub-field that matched (see walkthroughSearchAndAPIAdaptor) so matches can be grouped under
// a header that says WHERE in the task the match was — a comment match shows under "Comments", not the
// generic "Task answers". Keyed by the sub-field name (the part after `taskAnswers.`); '' is the legacy
// generic match with no sub-field. The array order drives the order the groups render in.
const taskAnswerSubFieldGroups: Array<{ subField: string; label: string }> = [
	{ subField: 'answer', label: 'Task answers' },
	{ subField: 'comments', label: 'Comments' },
	{ subField: 'checklistAnswers', label: 'Checklist answers' },
	{ subField: 'taskListAnswers', label: 'Task list answers' },
	{ subField: '', label: 'Task answers' },
];

// Number of task occurrences shown before the "+N more occurrences" toggle.
const HIGHLIGHT_OCCURRENCES_COLLAPSED = 1;

interface TaskAnswerHighlightOccurrence {
	siteSurveyTaskTitleId?: string;
	title?: string;
	groupTitle?: string;
}

// A single matched value (e.g. an answer of "1 (YES)") can occur across many tasks. The value is shown
// once and the individual task occurrences collapse behind a "+N more occurrences" toggle, so a common
// answer doesn't flood the row with identical pills. Each occurrence deep-links to its task on the
// detail page; expanding reveals every occurrence.
const TaskAnswerHighlightValueGroup: FC<{
	value: string;
	occurrences: TaskAnswerHighlightOccurrence[];
	walkthroughId: number | string;
	userType: string;
	pillClassName: string;
}> = ({ value, occurrences, walkthroughId, userType, pillClassName }) => {
	const [expanded, setExpanded] = React.useState(false);
	const total = occurrences.length;
	const visibleOccurrences = expanded
		? occurrences
		: occurrences.slice(0, HIGHLIGHT_OCCURRENCES_COLLAPSED);
	const hiddenCount = total - visibleOccurrences.length;

	const handleToggle = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setExpanded((previous) => !previous);
	};

	return (
		<div className="flex min-w-0 flex-col items-start gap-1">
			<div className="flex min-w-0 flex-col items-start gap-1">
				{visibleOccurrences.map((occurrence, index) => {
					const siteSurveyTaskTitleId = nullSafeGet('siteSurveyTaskTitleId', occurrence);
					const taskTitle = nullSafeGet('title', occurrence);
					const taskGroupTitle = nullSafeGet('groupTitle', occurrence);
					const occurrenceLabel = (
						<>
							{taskGroupTitle ? <span className="text-grey-6">{taskGroupTitle}: </span> : null}
							{taskTitle || 'View task'}
						</>
					);
					return siteSurveyTaskTitleId ? (
						<Link
							key={index}
							to={`/${userType}/siteSurveys/walkthrough/detail/${walkthroughId}/details?taskTitleId=${encodeURIComponent(
								siteSurveyTaskTitleId
							)}`}
							onClick={(event) => event.stopPropagation()}
							className="footnoteregular block max-w-full truncate text-grey-7 hover:underline"
						>
							{occurrenceLabel}
						</Link>
					) : (
						<span key={index} className="footnoteregular block max-w-full truncate text-grey-7">
							{occurrenceLabel}
						</span>
					);
				})}
				{total > HIGHLIGHT_OCCURRENCES_COLLAPSED ? (
					<span
						role="button"
						tabIndex={0}
						onClick={handleToggle}
						className="footnoteregular cursor-pointer text-grey-8 underline"
					>
						{expanded ? 'Show less' : `+${hiddenCount} more occurrences`}
					</span>
				) : null}
			</div>
			<span className={pillClassName}>
				<span
					className="highlights footnoteMedium min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-grey-8"
					dangerouslySetInnerHTML={{ __html: value }}
				/>
			</span>
		</div>
	);
};

const getCompletionPercentage = (walkthrough) => {
	if (walkthrough.taskAnswersCount !== undefined) {
		const total = walkthrough.taskAnswersCount || 0;
		const completed = walkthrough.taskAnswersCompletedCount || 0;
		return total > 0 ? completed / (total * 1.0) : 0;
	}
	const taskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
	if (!taskAnswers.length) return 0;
	return taskAnswers.filter(isWalthroughTaskAnswered).length / (taskAnswers.length * 1.0);
};

const getStatusText = (walkthrough) => {
	const completionPercentage = getCompletionPercentage(walkthrough);

	if (walkthrough.status === 'complete') {
		return 'Completed';
	} else if (completionPercentage === 0 || completionPercentage === Infinity) {
		return 'Not Started';
	} else {
		return `${floatToPercentageString(completionPercentage, 0)} Answered`;
	}
};

export const SimpleWalkthroughRowDisplay: FC<any> = ({ walkthrough, walkthroughLink }) => {
	const statusText = getStatusText(walkthrough);
	return (
		<div className="flex flex-row items-center">
			<HyperLink newTabPath={walkthroughLink} text={walkthrough.title} />
			<div className="ml-4">
				<WalkthroughStatusDisplay status={statusText} displayText={statusText} />
			</div>
		</div>
	);
};

export const WalkthroughRowDisplay: React.FC<any> = ({ walkthrough, ...props }) => {
	const statusText = getStatusText(walkthrough);
	const { highlight } = walkthrough;
	// The OpenSearch highlight bag returns each logical field under several analyzer
	// variants (`taskAnswers`, `taskAnswers.prefixed`, `taskAnswers.stemmed`, …).
	// Collapse them to single base keys so the grid below shows each field once.
	const collapsedHighlight = collapseHighlightAnalyzerVariants(highlight);

	const taskAnswers = nullSafeGetOrElse('taskAnswers', walkthrough, []);
	const sumOfComments =
		walkthrough.commentsCount !== undefined
			? walkthrough.commentsCount
			: taskAnswers.reduce((number, task) => (task.comments ? 1 : 0) + number, 0);

	const sumOfAttachments =
		walkthrough.attachmentsCount !== undefined
			? walkthrough.attachmentsCount
			: taskAnswers.reduce(
					(number, task) => nullSafeGetOrElse('attachments.length', task, 0) + number,
					0
			  );

	const workOrderPopoverData = walkthrough.workOrderCountsByDisplayStatus
		? Object.entries(walkthrough.workOrderCountsByDisplayStatus).flatMap(([displayStatus, count]) =>
				Array.from({ length: count as number }, () => ({ displayStatus }))
		  )
		: taskAnswers.flatMap((task) => (task.hasOwnProperty('workOrders') ? task.workOrders : []));

	// Nested taskAnswers matches, each paired with its siteSurveyTaskTitleId (see the search adaptor).
	// Lives under `highlight` (not a top-level field) so it survives the /read round-trip — see
	// getEntitiesByIdWithSearch, which only re-applies `highlight` onto a previously-read record.
	// Rendered as clickable pills that deep-link to the exact task on the detail page.
	const taskAnswerHighlights = nullSafeGetOrElse('taskAnswerHighlights', highlight, []);
	// Note-text matches, each paired with its noteId (see the search adaptor). Like taskAnswerHighlights,
	// this lives under `highlight` so it survives the /read round-trip (getEntitiesByIdWithSearch only
	// re-applies `highlight`). Rendered as pills that deep-link to the exact note on the Notes tab.
	const noteHighlights = nullSafeGetOrElse('noteHighlights', highlight, []);
	const userType = getUserTypeFromURL(window.location);
	const taskAnswerPillClassName =
		'inline-flex h-[28px] min-w-0 max-w-full items-center rounded-[12px] bg-grey-3 px-3';
	const hasNotesHighlights = noteHighlights.length > 0;
	const hasOtherHighlights = Object.keys(collapsedHighlight).some(
		(key) =>
			!displayedFields[key] && highlightLabels[key] && (collapsedHighlight[key] || []).length > 0
	);
	const showHighlightGrid =
		hasNotesHighlights || hasOtherHighlights || taskAnswerHighlights.length > 0;

	const overallScoreObject = getWalkthroughScoreObject(walkthrough);

	return (
		<div className="walkthroughRowDisplayBuyer group" {...props}>
			<div className="walkthroughRowDisplay__leftSide">
				<div className="walkthroughRowDisplay__rowTitle" style={{ lineHeight: '18.0px' }}>
					<Ellipsis tooltip={walkthrough.title} fullWidthRecognition={true} lines={1}>
						{renderWithHighlight(highlight, 'title', walkthrough.title)}
					</Ellipsis>
				</div>
				<div className="walkthroughRowDisplay__rowInlineGroup">
					<div className="walkthroughRowDisplay__grayChip">
						{renderWithHighlight(
							highlight,
							'locationName',
							nullSafeGet('location.name', walkthrough)
						)}
					</div>
					<div className="walkthroughRowDisplay__text">
						<WalkthroughStatusDisplay status={statusText} displayText={statusText} />
					</div>
					{walkthrough.status === 'complete' ? (
						<div className="walkthroughRowDisplay__text">
							<span>
								Marked Complete
								{walkthrough.completedAt ? (
									<>
										{' '}
										<DateTimeHover
											timestamp={nullSafeGet('completedAt', walkthrough)}
											showDate
											timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
										/>
									</>
								) : (
									''
								)}
								{` `}
								{walkthrough.completedBy ? (
									<span>
										{'by '}
										<UserNameWithEmail
											email={walkthrough.completedBy}
											mode={'inline'}
											placement={'top'}
										/>
									</span>
								) : null}
							</span>
						</div>
					) : null}
					<div className="walkthroughRowDisplay__text">
						<span>
							{`Created `}{' '}
							<DateTimeHover
								timestamp={nullSafeGet('createdAt', walkthrough)}
								showDate
								timeZone={nullSafeGet('location.timeZoneId', walkthrough)}
							/>
						</span>
					</div>
				</div>
				<div className="pt-2">
					<WalkthroughRowNotePreview walkthrough={walkthrough} />
				</div>
				{showHighlightGrid ? (
					// Search-highlight mode: every hit is rendered as a uniform pill chip in a
					// two-column grid (label column + content column), matching the redesigned
					// work order row (see WorkOrderRowDisplay). A single continuous separator
					// tracks the label-column width via GridColumnSeparator.
					<div
						className="mt-2"
						style={{
							display: 'grid',
							gridTemplateColumns: 'auto 1fr',
							columnGap: 12,
							rowGap: 12,
							position: 'relative',
						}}
					>
						<GridColumnSeparator />

						{/* Notes row — one pill per matched note (backend returns one entry per note). */}
						{noteHighlights.length > 0 ? (
							<>
								<span className="footnoteregular shrink-0 self-start whitespace-nowrap pr-3 pt-1 text-grey-8">
									Notes:
								</span>
								<div className="flex min-w-0 flex-col items-start gap-2 py-1">
									{noteHighlights.map((noteHighlight, index) => {
										const noteValue = nullSafeGet('value', noteHighlight);
										const noteId = nullSafeGet('noteId', noteHighlight);
										const notePillContent = (
											<span
												className="highlights footnoteMedium min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-grey-8"
												dangerouslySetInnerHTML={{ __html: noteValue }}
											/>
										);
										// Deep-link to the exact matched note on the Notes tab by its id; the notes view
										// scrolls to + flashes the note whose id matches (see CommonNotesChatPage).
										return noteId ? (
											<Link
												key={index}
												to={`/${userType}/siteSurveys/walkthrough/detail/${
													walkthrough.id
												}/notes?noteId=${encodeURIComponent(noteId)}`}
												onClick={(event) => event.stopPropagation()}
												className={`${taskAnswerPillClassName} hover:bg-grey-4`}
											>
												{notePillContent}
											</Link>
										) : (
											<span key={index} className={taskAnswerPillClassName}>
												{notePillContent}
											</span>
										);
									})}
								</div>
							</>
						) : null}

						{/* Other highlight sections (Description, Template, Task answers, etc.) */}
						{Object.keys(collapsedHighlight).map((key) => {
							if (!displayedFields[key] && highlightLabels[key]) {
								const entries = collapsedHighlight[key] || [];
								if (!entries.length) return null;
								return (
									<React.Fragment key={key}>
										<span className="footnoteregular shrink-0 self-start whitespace-nowrap pr-3 pt-1 text-grey-8">
											{highlightLabels[key]}:
										</span>
										<div className="flex min-w-0 flex-col items-start gap-2 py-1">
											{entries.map((entry, index) => (
												<span
													key={index}
													className="inline-flex h-[28px] min-w-0 max-w-full items-center rounded-[12px] bg-grey-3 px-3"
												>
													<span
														className="highlights footnoteMedium min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-grey-8"
														dangerouslySetInnerHTML={{ __html: entry }}
													/>
												</span>
											))}
										</div>
									</React.Fragment>
								);
							}
							return null;
						})}

						{/* Task-answer matches — grouped by the sub-field that matched, each under its own
						    header (Task answers / Comments / Checklist answers / Task list answers) so a comment
						    hit doesn't read as a "Task answers" hit. Pills deep-link to the matched task on the
						    detail page (scrolls to + flashes the task via the ?taskTitleId query param). */}
						{taskAnswerSubFieldGroups.map(({ subField, label }) => {
							const groupHighlights = taskAnswerHighlights.filter(
								(taskAnswerHighlight) =>
									nullSafeGetOrElse('subField', taskAnswerHighlight, '') === subField
							);
							if (!groupHighlights.length) return null;
							// Group identical matched values (e.g. the many tasks answered "1 (YES)") so each value
							// renders once with a "+N more occurrences" toggle instead of one pill per task. A Map
							// preserves first-seen order.
							const occurrencesByValue = new Map<string, TaskAnswerHighlightOccurrence[]>();
							groupHighlights.forEach((taskAnswerHighlight) => {
								const value = nullSafeGetOrElse('value', taskAnswerHighlight, '');
								if (!occurrencesByValue.has(value)) occurrencesByValue.set(value, []);
								occurrencesByValue.get(value).push(taskAnswerHighlight);
							});
							return (
								<React.Fragment key={subField || 'generic'}>
									<span className="footnoteregular shrink-0 self-start whitespace-nowrap pr-3 pt-1 text-grey-8">
										{label}:
									</span>
									<div className="flex min-w-0 flex-col items-start gap-2 py-1">
										{Array.from(occurrencesByValue.entries()).map(([value, occurrences]) => (
											<TaskAnswerHighlightValueGroup
												key={value}
												value={value}
												occurrences={occurrences}
												walkthroughId={walkthrough.id}
												userType={userType}
												pillClassName={taskAnswerPillClassName}
											/>
										))}
									</div>
								</React.Fragment>
							);
						})}
					</div>
				) : null}
			</div>
			<div className="walkthroughRowDisplay__rightSide flex flex-grow-0">
				<div className="walkthroughRowDisplay__extra1">
					<div className="walkthroughRowDisplay__pill">
						<div
							style={{
								display: 'flex',
								gap: 16,
								flexGrow: 1,
								justifyContent: 'flex-end',
								alignContent: 'center',
							}}
						>
							{sumOfComments > 0 ? (
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
									}}
								>
									<div
										style={{
											minWidth: 10,
											margin: '0px',
										}}
									>
										{sumOfComments}
									</div>
									<i
										style={{
											fontSize: 20,
											verticalAlign: 'middle',
											cursor: 'pointer',
											color: '#999',
											margin: '0px 2px',
											paddingLeft: '4px',
											paddingRight: '4px',
										}}
										className="icons8-font icons8-speech-bubble"
									/>
								</div>
							) : null}
							{sumOfAttachments > 0 ? (
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
									}}
								>
									<div
										style={{
											minWidth: 10,
											margin: '0px',
										}}
									>
										{sumOfAttachments}
									</div>
									<i
										style={{
											fontSize: 20,
											verticalAlign: 'middle',
											cursor: 'pointer',
											color: '#999',
											margin: '0px 2px',
											paddingLeft: '4px',
											paddingRight: '4px',
										}}
										className="icons8-font icons8-compact-camera"
									/>
								</div>
							) : null}
							<OWInsightPopover
								title={'Work Orders'}
								breakdown={'displayStatus'}
								labels={UNFINISHED_WORK_ORDER_DISPLAY_STATUS_DISPLAY_NAMES}
								icon={'icons8-wrench'}
								defaultData={workOrderPopoverData}
							/>
							<WalkthroughScoreDisplay
								scoreObject={overallScoreObject}
								isPill={true}
								walkthrough={walkthrough}
							/>
							<WalkthroughTaskListScorePill walkthrough={walkthrough} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
